import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';

const TOS_VERSION = '1.0';
const LOCAL_KEY = 'tos_accepted_v1';

async function checkAcceptance(userId: string): Promise<boolean> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data } = await (supabase as any)
    .from('user_tos_acceptances')
    .select('id')
    .eq('user_id', userId)
    .eq('tos_version', TOS_VERSION)
    .maybeSingle();
  return !!data;
}

export function useTosAcceptance() {
  const { user, loading: authLoading } = useAuth();
  const [needsAcceptance, setNeedsAcceptance] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;

    if (localStorage.getItem(LOCAL_KEY)) {
      setLoading(false);
      return;
    }

    if (!user) {
      setNeedsAcceptance(false);
      setLoading(false);
      return;
    }

    let cancelled = false;
    checkAcceptance(user.id).then((accepted) => {
      if (cancelled) return;
      if (accepted) {
        localStorage.setItem(LOCAL_KEY, 'true');
      } else {
        setNeedsAcceptance(true);
      }
      setLoading(false);
    });

    return () => { cancelled = true; };
  }, [user, authLoading]);

  const acceptTos = useCallback(async () => {
    localStorage.setItem(LOCAL_KEY, 'true');
    setNeedsAcceptance(false);

    if (!user) return;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    await (supabase as any).from('user_tos_acceptances').upsert({
      user_id: user.id,
      tos_version: TOS_VERSION,
      accepted_at: new Date().toISOString(),
    }, { onConflict: 'user_id,tos_version' });
  }, [user]);

  return { needsAcceptance, loading, acceptTos };
}
